import React, {useEffect, useRef, useState} from 'react';
import {TransformValues} from '../../utils/helper';
import 'react-color-palette/css';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../store';
import BackgroundTexturesDisplay from './backgroundTexturesDisplay';
import {BackgroundUpload, UnsplashLogo} from '../../ui-components/Iconpack';
import {Dialog, DialogContent} from 'src/ui-components/Modal';
import UnsplashImageSearch from '../unsplash/unsplash';
import {toast, ToastContainer} from 'react-toastify';
import {getBackgroundFiles, unsplashTracker, uploadFile} from '../../actions/media';
import {FileUploadingBig, SpinLoader} from '../../ui-components/loaders';
import {BGSampleImageInfo} from '../../reducers/media';
import {UnsplashPhotos} from '../../reducers/model';
import {TransformationType} from '../../screens/TransformationV2/Transformation';

interface BackgroundTextureProps {
    onTransformValuesChange: (values: {backgroundImageUid?: string}) => void;
    transformationObject: TransformValues;
    addTransformation: (t: TransformationType) => void;
}

interface BackgroundData {
    [key: string]: BGSampleImageInfo[];
}
export default function BackgroundReplacementTexture({
    onTransformValuesChange,
    transformationObject,
    addTransformation,
}: BackgroundTextureProps) {
    const [backgroundImageUid, setBackgroundImageUid] = useState('');
    const [convertedImages, setConvertedImages] = useState<BackgroundData>({});
    const [files, setFiles] = useState<FileList | null>();
    const fileButtonUploadRef = useRef<any>(null);
    const [bgUploadingStatus, setBgUploadingStatus] = useState(false);
    const [modalClosed, setModalClosed] = useState(true);
    const [unsplashSelectedImages, setUnsplashSelectedImages] = useState<UnsplashPhotos[]>([]);
    const [showSpinLoader, setShowSpinLoader] = useState(false);

    let mediaProps = useSelector((state: RootState) => state.media);
    const dispatch = useAppDispatch();
    const orgUid = transformationObject.orgUid!;

    const capitalizeFirstLetter = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };
    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target?.files;
        setFiles(files);
    };

    const handleUnsplashSelection = (selectedImages: UnsplashPhotos[]) => {
        setUnsplashSelectedImages(selectedImages);
    };
    const convertToSamples = (data: BGSampleImageInfo[]) => {
        const backgrounds: BackgroundData = {};
        backgrounds['Custom'] = [];

        data.forEach((image) => {
            const prefix = capitalizeFirstLetter(image.theme ? image.theme : 'custom');
            const thumbnailUrlWithoutParams = image.thumbnail_url.split('?')[0];
            const modifiedImage = {...image, thumbnail_url: thumbnailUrlWithoutParams};
            if (!backgrounds[prefix]) {
                backgrounds[prefix] = [];
            }
            backgrounds[prefix].push(modifiedImage);
        });
        setShowSpinLoader(false);
        return backgrounds;
    };

    const assignBackgroundImageUid = (imageUid: string) => {
        addTransformation(TransformationType.BG_IMG);
        onTransformValuesChange({backgroundImageUid: imageUid});
    };

    const handleButtonUpload = () => {
        fileButtonUploadRef?.current?.click();
    };

    const onUploading = async (fileData: any) => {
        setBgUploadingStatus(true);
        await Promise.all(
            fileData.map(async (fileToUpload: {toUploadFile: any; file: any}) => {
                await dispatch(uploadFile({params: {file: fileToUpload?.file, orgUid: orgUid, uploaded_type: '3'}}));
            }),
        );
    };
    const processFileToUpload = (data: FileList | null | undefined | File[]) => {
        const finalFilesArray: {
            file: File;
            options: {fileName: string; fileSize: number; fileType: string};
        }[] = [];
        if (data && data.length > 0) {
            const filesArray = Array.from(data);
            for (const file of filesArray) {
                if (file.size > 16000000) {
                    toast.error(`File ${file.name} exceeds 16 MB. Please try with a smaller file.`);
                } else {
                    finalFilesArray.unshift({
                        file,
                        options: {
                            fileName: file.name,
                            fileSize: file.size,
                            fileType: file.type,
                        },
                    });
                }
            }
        }

        onUploading(finalFilesArray).then(async () => {
            setBgUploadingStatus(false);
            dispatch(getBackgroundFiles(orgUid));
        });
    };

    useEffect(() => {
        if (unsplashSelectedImages.length) {
            const unsplashFiles: File[] = [];
            const fetchPromises = unsplashSelectedImages.map((image) => {
                dispatch(unsplashTracker({image_id: image.id}));
                const url = new URL(image.urls.regular);
                const pathnameParts = url.pathname.split('/');
                const filename = pathnameParts[pathnameParts.length - 1];
                return fetch(image.urls.raw).then(async (r) => {
                    const blob = await r.blob();
                    const fileType = blob.type.split('/')[1];
                    const fileName = `${filename}.${fileType}`;
                    const file = new File([blob], fileName, {type: blob.type});
                    unsplashFiles.push(file);
                });
            });

            Promise.all(fetchPromises)
                .then(() => {
                    setModalClosed(true);
                    processFileToUpload(unsplashFiles);
                })
                .catch((error) => {
                    console.error('Error fetching images:', error);
                });
        }
    }, [unsplashSelectedImages]);

    useEffect(() => {
        if (files && files.length > 0) {
            processFileToUpload(files);
            fileButtonUploadRef.current.value = null;
        }
    }, [files]);

    useEffect(() => {
        if (mediaProps.backgroundSampleFiles && mediaProps.backgroundSampleFiles.length) {
            setConvertedImages(convertToSamples(mediaProps.backgroundSampleFiles));
        }
    }, [mediaProps.backgroundSampleFiles]);

    useEffect(() => {
        if (transformationObject.backgroundImageUid) {
            setBackgroundImageUid(transformationObject.backgroundImageUid);
        }
        if (transformationObject.orgUid) {
            dispatch(getBackgroundFiles(transformationObject.orgUid));
            setShowSpinLoader(true);
        }
    }, []);

    return (
        <div className='relative'>
            {/*<Dialog>*/}
            <div className='mb-4 mt-4'>
                <span className='text-[12px] font-[600] text-[#FFFFFF]'>Add custom background</span>
                <div className='flex gap-3 pt-1'>
                    <div className='flex cursor-pointer flex-col items-center gap-[6px]' onClick={handleButtonUpload}>
                        <input
                            type='file'
                            accept='image/png, image/jpg, image/jpeg, image/webp'
                            multiple
                            hidden
                            onChange={handleFiles}
                            ref={fileButtonUploadRef}
                        />
                        <BackgroundUpload />
                        <span className='text-[12px] font-[400] text-[#FFFFFF]'>Upload</span>
                    </div>

                    <div className='hidden cursor-pointer flex-col gap-[6px]' onClick={() => setModalClosed(false)}>
                        <UnsplashLogo />
                        <div className='flex flex-col items-center justify-center'>
                            <span className='text-[12px] font-[400] text-[#FFFFFF]'>Select</span>
                            <span className='text-[12px] font-[400] text-[#FFFFFF]'>Unsplash</span>
                        </div>
                    </div>
                </div>
            </div>
            {!bgUploadingStatus ? (
                <div className='flex flex-col items-center justify-center gap-2'>
                    {!showSpinLoader ? (
                        <>
                            {convertedImages &&
                                Object.keys(convertedImages).map((prefix) => (
                                    <BackgroundTexturesDisplay
                                        key={prefix}
                                        name={prefix}
                                        value={convertedImages[prefix]}
                                        assignBackgroundImageUid={assignBackgroundImageUid}
                                        currentImageUid={backgroundImageUid}
                                    />
                                ))}
                        </>
                    ) : (
                        <div className='mt-[280px]'>
                            <SpinLoader />
                        </div>
                    )}
                </div>
            ) : (
                <Dialog open={bgUploadingStatus}>
                    <DialogContent className='h-[558px] w-[339px] rounded-[12px] md:h-[458px] md:w-[904px]'>
                        <div className='flex flex-col items-center justify-center'>
                            <FileUploadingBig />
                            <span className='text-[24px] font-[700] text-[#000000]'>Uploading Files</span>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <UnsplashImageSearch
                modalClosed={modalClosed}
                onUnsplashSelection={handleUnsplashSelection}
                onClose={() => setModalClosed(true)}
            />
        </div>
    );
}
